// modules
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants
import { RejectRetroClaim, Routes } from "constants/global";

// redux
import { rejectRetroClaim, retroClaimIDSelector, getLoadingStateSelector } from "redux/retroClaim/retroClaimSlice";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// utils
import { validateRejectRetroClaimReason } from "utils/Validations/fieldValidations";

type Input = {
	input: string;
	active: boolean;
};

export type RejectClaimPayload = {
	retro_claim_id: number;
	remarks: string;
};

export type LocationState = {
	state: {
		retroClaimId: number;
		throughCreateClaim: boolean;
	};
};

export function useRejectRetrospectiveClaim() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { state } = location as LocationState;

	const retroClaimId = useAppSelector(retroClaimIDSelector);
	const dataLoading = useAppSelector(getLoadingStateSelector);

	const [inputValue, setInputValue] = useState<Input>({ input: "", active: false });
	const [isBtnClicked, setIsBtnClicked] = useState<boolean>(false);
	const [isReasonValid, setIsReasonValid] = useState(false);

	const validateReason = useCallback(
		(value: string) => {
			const isValidReason = validateRejectRetroClaimReason(value);
			setIsReasonValid(isValidReason);
		},
		[inputValue, isReasonValid]
	);

	const handleInput = useCallback(
		(value: string) => {
			if (value) {
				setInputValue({ ...inputValue, input: value, active: true });
				validateReason(value);
			} else {
				setInputValue({ input: "", active: false });
				validateReason(value);
			}
		},
		[inputValue, setInputValue, isReasonValid, validateReason]
	);

	const goToRetroClaim = useCallback(() => {
		navigate(`${Routes.RETRO_CLAIM}`);
	}, [navigate, dispatch]);

	const goBack = useCallback(() => {
		goToRetroClaim();
	}, [navigate, goToRetroClaim]);

	const onFailure = useCallback(() => {
		navigate(`${Routes.RETRO_CLAIM}/${Routes.REJECT_RETRO_CLAIM}`);
	}, [navigate]);

	useEffect(() => {
		// Check if we have a retroClaimId from location state
		if (!state?.retroClaimId && !retroClaimId?.data?.retro_claim_id && !isBtnClicked) {
			goBack();
		}
	}, [retroClaimId, state]);

	const proceedToConfirm = useCallback(async () => {
		if (inputValue.active) {
			const claimId = state?.retroClaimId || retroClaimId?.data?.retro_claim_id;

			if (!claimId) {
				goBack();
				return;
			}

			const payload: RejectClaimPayload = {
				retro_claim_id: claimId,
				remarks: inputValue.input
			};
			setIsBtnClicked(true);
			try {
				const { errors, message } = await dispatch(rejectRetroClaim(payload)).unwrap();

				if (!errors?.length && message === RejectRetroClaim.CLAIM_SUCCESS_MESSAGE) {
					CountDownModal({
						timer: 3,
						title: RejectRetroClaim?.CLAIM_REJECTED,
						subtitle: RejectRetroClaim?.CLAIM_REJECTED_SUBTITLE,
						okButton: {
							label: RejectRetroClaim?.GOBACK_BUTTON,
							onClick: goToRetroClaim
						},
						onTimeComplete: goToRetroClaim
					});
				}
			} catch (error) {
				const err = error as any;
				if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						title: err?.response?.data?.errors[0]?.message,
						onTimeComplete: onFailure
					});
				}
			}
		}
	}, [inputValue, goBack, state, retroClaimId]);

	return {
		inputValue,
		loading: dataLoading === 1,
		isReasonValid,
		validateReason,
		handleInput,
		goBack,
		goToRetroClaim,
		onFailure,
		proceedToConfirm
	};
}
